import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../api";

const PROFILE_TYPES = ["Business", "Organization", "Individual"];

const ProfileModal = ({ profile, onClose, onSaved }) => {
  const isEdit = Boolean(profile);
  const [formData, setFormData] = useState({
    name: profile?.name || "",
    profile_type: profile?.profile_type || "Individual",
    description: profile?.description || "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let obj;
    if (isEdit && profile.logo_filename) {
      axios
        .get(`${API}/profiles/${profile.id}/logo`, { responseType: "blob" })
        .then((r) => {
          obj = URL.createObjectURL(r.data);
          setCurrentLogoUrl(obj);
        })
        .catch(() => {});
    }
    return () => {
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [isEdit, profile]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (JPG, PNG, GIF)");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("Logo file must be smaller than 1MB");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let profileId;
      if (isEdit) {
        await axios.put(`${API}/profiles/${profile.id}`, formData);
        profileId = profile.id;
      } else {
        const res = await axios.post(`${API}/profiles`, formData);
        profileId = res.data.id;
      }
      if (logoFile) {
        const fd = new FormData();
        fd.append("logo", logoFile);
        await axios.post(`${API}/profiles/${profileId}/logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await onSaved(profileId);
      onClose();
      toast.success(isEdit ? "Profile updated successfully!" : "Profile created successfully!");
    } catch (err) {
      console.error("Failed to save profile:", err);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the profile "${profile.name}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/profiles/${profile.id}`);
      await onSaved(null);
      onClose();
      toast.success("Profile deleted successfully");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="profile-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{isEdit ? "Edit Profile" : "Add Profile"}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Logo (Optional)</label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="w-16 h-16 rounded-lg object-contain border bg-white" />
                ) : currentLogoUrl ? (
                  <img src={currentLogoUrl} alt="Current logo" className="w-16 h-16 rounded-lg object-contain border bg-white" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border text-lg font-semibold text-gray-400">
                    {(formData.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" id="profile-logo-upload" data-testid="profile-logo-input" />
                <label htmlFor="profile-logo-upload" className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  {logoFile || currentLogoUrl ? "Change Logo" : "Upload Logo"}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF up to 1MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              required
              data-testid="profile-name-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              data-testid="profile-type-select"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.profile_type}
              onChange={(e) => setFormData({ ...formData, profile_type: e.target.value })}
            >
              {PROFILE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
            <textarea
              data-testid="profile-description-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-between items-center pt-4">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  data-testid="profile-delete-button"
                  className="px-4 py-2 border border-red-300 rounded-md text-red-700 hover:bg-red-50 text-sm disabled:opacity-50"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                data-testid="profile-cancel-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="profile-submit-button"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : isEdit ? "Update Profile" : "Create Profile"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
